import { create } from "zustand";
import type {
  CollaborateListView,
  CollaborateUserRef,
  CommentReply,
  ComposerSnippet,
  CreateThreadInput,
  Thread,
  ThreadStatus,
  UserId,
} from "../types/collaborateThread";
import type { CollaborateUserProfile } from "../types/collaborateThread";
import type { ThreadFilterSummary, ThreadListPage } from "../api/paginationTypes";
import { wrapMentionsInBody } from "../utils/wrapMentionsInBody";

export type ThreadsPaginationState = {
  cursor: string | null;
  hasMore: boolean;
  loadingInitial: boolean;
  loadingMore: boolean;
  total: number;
};

export type FilterSummaryState = {
  listViewCounts: Record<CollaborateListView, number>;
  versionCounts: { versionNumber: number; count: number }[];
};

const EMPTY_LIST_VIEW_COUNTS: Record<CollaborateListView, number> = {
  comments: 0,
  unread: 0,
  mentions: 0,
  "all-approvals": 0,
  "pending-mine": 0,
  "requested-by-me": 0,
};

const EMPTY_FILTER_SUMMARY: FilterSummaryState = {
  listViewCounts: EMPTY_LIST_VIEW_COUNTS,
  versionCounts: [],
};

const EMPTY_THREADS_PAGINATION: ThreadsPaginationState = {
  cursor: null,
  hasMore: false,
  loadingInitial: false,
  loadingMore: false,
  total: 0,
};

type CollaborateState = {
  hydrated: boolean;
  loading: boolean;
  usersById: Record<UserId, CollaborateUserProfile>;
  currentUserId: UserId;
  threadsById: Record<string, Thread>;
  threadIds: string[];
  threadsPagination: ThreadsPaginationState;
  filterSummary: FilterSummaryState;
  activeThreadId: string | null;
  listView: CollaborateListView;
  /** null = all contract versions */
  versionFilter: number | null;
  composerOpen: boolean;
  composerSnippet: ComposerSnippet | null;

  hydrate: (payload: {
    users: CollaborateUserProfile[];
    currentUserId: UserId;
    threads: Thread[];
    pagination?: Partial<ThreadsPaginationState>;
    filterSummary?: FilterSummaryState;
  }) => void;
  resetThreadPages: () => void;
  setInitialThreadPage: (page: ThreadListPage, summary?: ThreadFilterSummary) => void;
  appendThreadPage: (page: ThreadListPage) => void;
  mergeThreadUpdates: (threads: Thread[]) => void;
  setFilterSummary: (summary: ThreadFilterSummary) => void;
  setThreadsPagination: (patch: Partial<ThreadsPaginationState>) => void;
  syncThreads: (threads: Thread[]) => void;
  setLoading: (loading: boolean) => void;
  setListView: (view: CollaborateListView) => void;
  setVersionFilter: (version: number | null) => void;
  openThread: (id: string) => void;
  focusThread: (id: string, currentVersionId: string) => Promise<void>;
  locateThreadInDocument: (id: string, currentVersionId: string) => Promise<void>;
  closeThread: () => void;
  openComposer: (snippet?: ComposerSnippet | null) => void;
  closeComposer: () => void;
  addThread: (input: CreateThreadInput) => Thread;
  addReply: (threadId: string, body: string) => void;
  applyThreadFromServer: (thread: Thread) => void;
  applyReplyFromServer: (threadId: string, reply: CommentReply) => void;
  setThreadStatus: (threadId: string, status: ThreadStatus) => void;
  markThreadRead: (threadId: string) => void;
  pruneReadThreadsFromUnreadList: () => void;
  getThread: (id: string) => Thread | undefined;
  getThreadsOrdered: () => Thread[];
  resetCollaborate: () => void;
};

function newId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, "0")}`;
}

function normalizeFilterSummary(summary: ThreadFilterSummary): FilterSummaryState {
  return {
    listViewCounts: {
      comments: summary.listViewCounts.comments ?? 0,
      unread: summary.listViewCounts.unread ?? 0,
      mentions: summary.listViewCounts.mentions ?? 0,
      "all-approvals": summary.listViewCounts["all-approvals"] ?? 0,
      "pending-mine": summary.listViewCounts["pending-mine"] ?? 0,
      "requested-by-me": summary.listViewCounts["requested-by-me"] ?? 0,
    },
    versionCounts: summary.versionCounts ?? [],
  };
}

function pageToStorePatch(page: ThreadListPage): {
  threadsById: Record<string, Thread>;
  threadIds: string[];
  threadsPagination: ThreadsPaginationState;
} {
  const threadsById: Record<string, Thread> = {};
  const threadIds: string[] = [];
  page.items.forEach((thread) => {
    threadsById[thread.id] = thread;
    threadIds.push(thread.id);
  });
  return {
    threadsById,
    threadIds,
    threadsPagination: {
      cursor: page.nextCursor,
      hasMore: page.hasMore,
      loadingInitial: false,
      loadingMore: false,
      total: page.total,
    },
  };
}

function pruneUnreadThreadIds(
  listView: CollaborateListView,
  threadIds: string[],
  threadsById: Record<string, Thread>,
): string[] {
  if (listView !== "unread") {
    return threadIds;
  }
  return threadIds.filter((id) => threadsById[id]?.unread === true);
}

export const useCollaborateStore = create<CollaborateState>((set, get) => ({
  hydrated: false,
  loading: false,
  usersById: {},
  currentUserId: "",
  threadsById: {},
  threadIds: [],
  threadsPagination: EMPTY_THREADS_PAGINATION,
  filterSummary: EMPTY_FILTER_SUMMARY,
  activeThreadId: null,
  listView: "comments",
  versionFilter: null,
  composerOpen: false,
  composerSnippet: null,

  hydrate: ({ users, currentUserId, threads, pagination, filterSummary }) => {
    const usersById: Record<UserId, CollaborateUserProfile> = {};
    users.forEach((u) => {
      usersById[u.id] = u;
    });
    const threadsById: Record<string, Thread> = {};
    const threadIds: string[] = [];
    threads.forEach((t) => {
      threadsById[t.id] = t;
      threadIds.push(t.id);
    });
    set({
      hydrated: true,
      loading: false,
      usersById,
      currentUserId,
      threadsById,
      threadIds,
      threadsPagination: {
        ...EMPTY_THREADS_PAGINATION,
        ...pagination,
        loadingInitial: false,
        loadingMore: false,
      },
      filterSummary: filterSummary ?? EMPTY_FILTER_SUMMARY,
    });
  },

  resetThreadPages: () =>
    set({
      threadsById: {},
      threadIds: [],
      threadsPagination: {
        ...EMPTY_THREADS_PAGINATION,
        loadingInitial: true,
      },
    }),

  setInitialThreadPage: (page, summary) => {
    const patch = pageToStorePatch(page);
    set({
      threadsById: patch.threadsById,
      threadIds: patch.threadIds,
      threadsPagination: patch.threadsPagination,
      hydrated: true,
      loading: false,
      ...(summary ? { filterSummary: normalizeFilterSummary(summary) } : {}),
    });
  },

  appendThreadPage: (page) => {
    const { threadsById, threadIds } = get();
    const nextById = { ...threadsById };
    const nextIds = [...threadIds];
    page.items.forEach((thread) => {
      if (!nextById[thread.id]) {
        nextIds.push(thread.id);
      }
      nextById[thread.id] = thread;
    });
    set({
      threadsById: nextById,
      threadIds: nextIds,
      threadsPagination: {
        ...get().threadsPagination,
        cursor: page.nextCursor,
        hasMore: page.hasMore,
        total: page.total,
        loadingMore: false,
      },
    });
  },

  mergeThreadUpdates: (threads) => {
    if (threads.length === 0) {
      return;
    }
    const { threadsById, threadIds } = get();
    const nextById = { ...threadsById };
    let nextIds = threadIds;
    threads.forEach((thread) => {
      nextById[thread.id] = thread;
      if (!threadIds.includes(thread.id)) {
        nextIds = [thread.id, ...nextIds];
      }
    });
    const { listView } = get();
    nextIds = pruneUnreadThreadIds(listView, nextIds, nextById);
    set({ threadsById: nextById, threadIds: nextIds, hydrated: true });
  },

  pruneReadThreadsFromUnreadList: () => {
    const { listView, threadIds, threadsById, threadsPagination } = get();
    if (listView !== "unread") {
      return;
    }
    const nextIds = pruneUnreadThreadIds(listView, threadIds, threadsById);
    if (nextIds.length === threadIds.length) {
      return;
    }
    set({
      threadIds: nextIds,
      threadsPagination: {
        ...threadsPagination,
        total: Math.max(0, threadsPagination.total - (threadIds.length - nextIds.length)),
      },
    });
  },

  setFilterSummary: (summary) => set({ filterSummary: normalizeFilterSummary(summary) }),

  setThreadsPagination: (patch) =>
    set({ threadsPagination: { ...get().threadsPagination, ...patch } }),

  syncThreads: (threads) => {
    get().mergeThreadUpdates(threads);
  },

  setLoading: (loading) => set({ loading }),

  setListView: (view) =>
    set({
      listView: view,
      activeThreadId: null,
      composerOpen: false,
      composerSnippet: null,
    }),

  setVersionFilter: (version) => set({ versionFilter: version }),

  openThread: (id) => {
    get().markThreadRead(id);
    set({ activeThreadId: id, composerOpen: false, composerSnippet: null });
  },

  focusThread: async (id, currentVersionId) => {
    get().openThread(id);
    const thread = get().threadsById[id];
    if (!thread) {
      return;
    }
    const { resolveThreadAnchor } = await import("../utils/resolveThreadAnchor");
    const anchor = resolveThreadAnchor(thread);
    if (!anchor) {
      return;
    }
    const { jumpToThreadAnchor } = await import("../utils/jumpToThreadAnchor");
    await jumpToThreadAnchor(id, anchor, currentVersionId);
  },

  locateThreadInDocument: async (id, currentVersionId) => {
    get().markThreadRead(id);
    const thread = get().threadsById[id];
    if (!thread) {
      return;
    }
    const { resolveThreadAnchor } = await import("../utils/resolveThreadAnchor");
    const anchor = resolveThreadAnchor(thread);
    if (!anchor) {
      return;
    }
    const { jumpToThreadAnchor } = await import("../utils/jumpToThreadAnchor");
    await jumpToThreadAnchor(id, anchor, currentVersionId);
  },

  closeThread: () => set({ activeThreadId: null }),

  openComposer: (snippet) =>
    set({
      composerOpen: true,
      composerSnippet: snippet ?? { quote: "" },
      activeThreadId: null,
    }),

  closeComposer: () => set({ composerOpen: false, composerSnippet: null }),

  addThread: (input) => {
    const { currentUserId, usersById, threadsById, threadIds } = get();
    const creator = usersById[currentUserId];
    const now = new Date().toISOString();
    const snippet = input.snippet;
    const createdBy: CollaborateUserRef = creator
      ? { id: creator.id, name: creator.name, email: creator.email }
      : { id: currentUserId, name: "You", email: "" };

    const thread: Thread = {
      id: newId(),
      quote: snippet?.quote ?? "",
      body:
        input.body.includes("<mention>") || input.body.includes("<signoff>")
          ? input.body
          : wrapMentionsInBody(input.body),
      mentions: input.mentions,
      requireApproval: input.requireApproval,
      status: "pending",
      assignee: input.requireApproval ? input.assignee : null,
      createdBy,
      createdAt: now,
      versionNumber: 0,
      replies: [],
    };
    set({
      threadsById: { ...threadsById, [thread.id]: thread },
      threadIds: [thread.id, ...threadIds],
      composerOpen: false,
      composerSnippet: null,
    });
    return thread;
  },

  addReply: (threadId, body) => {
    const { threadsById, currentUserId, usersById } = get();
    const thread = threadsById[threadId];
    if (!thread) {
      return;
    }
    const author = usersById[currentUserId];
    const now = new Date().toISOString();
    const createdBy: CollaborateUserRef = author
      ? { id: author.id, name: author.name, email: author.email }
      : { id: currentUserId, name: "You", email: "" };
    const reply: CommentReply = {
      id: newId(),
      createdBy,
      createdAt: now,
      body:
        body.includes("<mention>") || body.includes("<signoff>")
          ? body
          : wrapMentionsInBody(body),
      versionNumber: 0,
    };
    const updated: Thread = {
      ...thread,
      replies: [...thread.replies, reply],
    };
    set({ threadsById: { ...threadsById, [threadId]: updated } });
  },

  applyThreadFromServer: (thread) => {
    const { threadsById, threadIds } = get();
    const ids = threadIds.includes(thread.id) ? threadIds : [thread.id, ...threadIds];
    set({
      threadsById: { ...threadsById, [thread.id]: thread },
      threadIds: ids,
      composerOpen: false,
      composerSnippet: null,
    });
  },

  applyReplyFromServer: (threadId, reply) => {
    const { threadsById } = get();
    const thread = threadsById[threadId];
    if (!thread) {
      return;
    }
    if (thread.replies.some((r) => r.id === reply.id)) {
      return;
    }
    set({
      threadsById: {
        ...threadsById,
        [threadId]: { ...thread, replies: [...thread.replies, reply] },
      },
    });
  },

  setThreadStatus: (threadId, status) => {
    const { threadsById } = get();
    const thread = threadsById[threadId];
    if (!thread) {
      return;
    }
    set({
      threadsById: {
        ...threadsById,
        [threadId]: { ...thread, status },
      },
    });
  },

  markThreadRead: (threadId) => {
    const thread = get().threadsById[threadId];
    if (!thread?.unread) {
      return;
    }
    const { filterSummary, listView, threadIds, threadsPagination } = get();
    const nextThreadIds =
      listView === "unread" ? threadIds.filter((id) => id !== threadId) : threadIds;
    const evictedCount = listView === "unread" ? threadIds.length - nextThreadIds.length : 0;
    set({
      threadsById: {
        ...get().threadsById,
        [threadId]: { ...thread, unread: false },
      },
      threadIds: nextThreadIds,
      filterSummary: {
        ...filterSummary,
        listViewCounts: {
          ...filterSummary.listViewCounts,
          unread: Math.max(0, filterSummary.listViewCounts.unread - 1),
        },
      },
      ...(evictedCount > 0
        ? {
            threadsPagination: {
              ...threadsPagination,
              total: Math.max(0, threadsPagination.total - evictedCount),
            },
          }
        : {}),
    });
    void import("../api").then(({ getCollaborateApi, isMockCollaborateApi }) => {
      const api = getCollaborateApi();
      if (isMockCollaborateApi(api)) {
        return;
      }
      void api.markThreadRead(threadId).catch(() => {
        // Best-effort; keep optimistic local clear.
      });
    });
  },

  getThread: (id) => get().threadsById[id],

  getThreadsOrdered: () => {
    const { threadIds, threadsById } = get();
    return threadIds.map((id) => threadsById[id]).filter(Boolean);
  },

  resetCollaborate: () =>
    set({
      hydrated: false,
      loading: false,
      usersById: {},
      currentUserId: "",
      threadsById: {},
      threadIds: [],
      threadsPagination: EMPTY_THREADS_PAGINATION,
      filterSummary: EMPTY_FILTER_SUMMARY,
      activeThreadId: null,
      listView: "comments",
      versionFilter: null,
      composerOpen: false,
      composerSnippet: null,
    }),
}));
